import { preciseDivide, soundManager } from '@slot-nx/core'
import { Container, DisplayObject, BitmapText } from 'pixi.js'
import gsap from 'gsap'
import { gameConfig } from '../../../config'
import { useGameStore } from '../../game/store/GameStore'
import { symbolUtil } from '../../spineManager'
import { preciseAdd } from '@slot-nx/core'
import { MultiplierType } from '../../spineManager/enum/animationType'
import { Spine } from '@esotericsoftware/spine-pixi'
import { sleep } from '../../game/util/gameUtil'
import { SweetBombSoundType } from '../../soundManager/enum/soundType'

export class MultiplierService {
  public multiplierReelRef: Container
  public multiplierHighLightNumberRef: BitmapText
  private startMultiplier: number
  private intervalValue: number
  private multiplierArray: number[] = []
  private remainArray: number[] = []

  public initMultiplierSymbol() {
    const mutiplierArray = useGameStore.getState().getMultiplierArray()
    this.getCurrentMultiplierReelResult()
    mutiplierArray.forEach(async (multiplierAmount, index) => {
      const spineObject = await symbolUtil.loadAllMultiplierSpine()
      spineObject.x = 0
      spineObject.y = 0
      spineObject.scale.set(0.9)
      this.asignMultiplierColor(multiplierAmount, spineObject)
      const multiplierFontSize = this.fontSize(multiplierAmount)
      const textObject = new BitmapText(`x${multiplierAmount}`, { fontName: 'FontW', fontSize: multiplierFontSize })
      textObject.anchor.set(0.5, 1.15)

      const nodeContainer = new Container()
      nodeContainer.x = gameConfig.nodeWidth * index
      nodeContainer.y = 0

      nodeContainer.addChild(spineObject, textObject)

      this.multiplierReelRef.addChild(nodeContainer)
      const multiplierText = preciseAdd(this.startMultiplier, this.intervalValue * index)
      this.multiplierArray.push(multiplierText)
    })
  }

  public fontSize(multiplierAmount: number) {
    const value = multiplierAmount
    switch (true) {
      case value >= 10 && value <= 99:
        return 32
      case value >= 100 && value <= 999:
        return 28
      case value >= 1000 && value <= 9909:
        return 24
      default:
        return 32
    }
  }

  private asignMultiplierColor(multiplierAmount: number, spineObject: Spine) {
    const colorOrder = this.determineColorOrder(multiplierAmount)
    const normalizedColorOrder = this.normalizeMultiplierAmount(colorOrder)
    const animationKey = `Multiple_${normalizedColorOrder}` as keyof typeof MultiplierType
    spineObject.state.setAnimation(0, MultiplierType[animationKey], false)
  }

  public getMultiplierAnimationStr(multiplierAmount: number, intervalValue: number) {
    this.intervalValue = intervalValue
    this.startMultiplier = intervalValue
    const colorOrder = this.determineColorOrder(multiplierAmount)
    const normalizedColorOrder = this.normalizeMultiplierAmount(colorOrder)
    return `Multiple_${normalizedColorOrder}`
  }

  private determineColorOrder(multiplierAmount: number): number {
    if (multiplierAmount === 1) {
      return 1
    }
    const multiplierIndex = Math.floor(preciseDivide(multiplierAmount, this.intervalValue))
    if (multiplierIndex < 18) {
      switch (this.startMultiplier) {
        case 1:
          return multiplierAmount
        case 2:
          return preciseAdd(multiplierIndex, 6)
        case 5:
          return preciseAdd(multiplierIndex, 12)
      }
    }
    return this.normalizeMultiplierAmount(multiplierIndex)
  }

  private normalizeMultiplierAmount(numerator: number) {
    let result = numerator
    if (result > 18) {
      result = ((numerator - 2) % 17) + 2
    }
    return result
  }

  public moveOneSymbol() {
    this.multiplierReelRef.children.forEach((multiplierSymbol) => {
      gsap.to(multiplierSymbol, {
        duration: 0.2,
        x: multiplierSymbol.x - gameConfig.nodeWidth,
        onComplete: () => {
          this.moveFirstSymbolToEnd(multiplierSymbol)
        },
      })
    })
  }

  private moveFirstSymbolToEnd(multiplierSymbol: DisplayObject) {
    if (multiplierSymbol.x === -gameConfig.nodeWidth) {
      multiplierSymbol.x = gameConfig.nodeWidth * 6
      multiplierSymbol.children?.forEach((child) => {
        const newMultiplier = preciseAdd(
          this.multiplierArray[this.multiplierArray.length - 1],
          this.intervalValue,
        )
        if (child instanceof Spine) {
          this.asignMultiplierColor(newMultiplier, child)
        }
        if (child instanceof BitmapText) {
          this.multiplierArray.shift()
          this.multiplierArray.push(newMultiplier)
          child.alpha = 1
          child.text = `x${newMultiplier}`
          child.fontSize = this.fontSize(newMultiplier)
        }
      })
    }
  }

  public clearReel() {
    soundManager.play(SweetBombSoundType.MultiplierSuckAway)
    return new Promise<void>((resolve) => {
      this.multiplierReelRef.children.forEach((multiplierSymbol, i) => {
        const positionIndex = preciseDivide(multiplierSymbol.x, gameConfig.nodeWidth)
        if (positionIndex === 6) {
          multiplierSymbol.alpha = 0
        }
        gsap.to(multiplierSymbol, {
          duration: 0.2,
          x: multiplierSymbol.x - gameConfig.nodeWidth * 7,
          onComplete: () => {
            multiplierSymbol.x = gameConfig.nodeWidth * 6 + gameConfig.nodeWidth * i
            resolve()
          },
        })
      })
    })
  }

  public insertReel() {
    this.multiplierReelRef.children.forEach((multiplierSymbol, i) => {
      gsap.to(multiplierSymbol, {
        duration: 0.2,
        alpha: 1,
        x: gameConfig.nodeWidth * i,
      })
    })
  }

  public setMultiplierResult(isOnPlaceOrderFail: boolean) {
    return new Promise<void>((resolve) => {
      this.getCurrentMultiplierReelResult()
      this.multiplierReelRef.children.forEach((multiplierSymbol, i) => {
        multiplierSymbol.children?.forEach((child) => {
          const currentNum = isOnPlaceOrderFail
            ? this.remainArray[0] + this.intervalValue * i
            : this.startMultiplier + this.intervalValue * i
          if (child instanceof Spine) {
            this.asignMultiplierColor(currentNum, child)
          }
          if (child instanceof BitmapText) {
            child.text = `x${currentNum}`
            child.fontSize = this.fontSize(currentNum)
            this.multiplierArray[i] = preciseAdd(this.startMultiplier, this.intervalValue * i)
            resolve()
          }
        })
      })
    })
  }

  private getCurrentMultiplierReelResult() {
    const { multiplierReelInfo } = useGameStore.getState()
    if (!multiplierReelInfo) return
    this.startMultiplier = multiplierReelInfo.startMultiplier
    this.intervalValue = multiplierReelInfo.intervalValue
  }

  public addBombNumToMultiplier(BombNum: number) {
    return new Promise<void>((resolve) => {
      this.multiplierArray = this.multiplierArray.map(number => preciseAdd(number, BombNum))
      this.multiplierReelRef.children.forEach(async (multiplierSymbol, i) => {
        const positionIndex = preciseDivide(multiplierSymbol.x, gameConfig.nodeWidth)
        multiplierSymbol.children?.forEach(async (child) => {
          if (child instanceof BitmapText) {
            if (positionIndex === 6) {
              child.text = `x${this.multiplierArray[positionIndex]}`
              child.fontSize = this.fontSize(this.multiplierArray[positionIndex])
              soundManager.play(SweetBombSoundType.AddMultiplier)
            }
            else {
              gsap.timeline()
                .to(child.scale, { duration: 0.4, x: 1.25, y: 1.25, ease: 'power2.out' })
                .to(child.scale, { duration: 0.25, x: 1.25, y: 1.25, onComplete: () => {
                  child.text = `x${this.multiplierArray[positionIndex]}`
                  child.fontSize = this.fontSize(this.multiplierArray[positionIndex])
                } })
                .to(child.scale, { duration: 0.05, x: 1.25, y: 1.25 })
                .to(child, {
                  duration: 0.1,
                  y: '-10',
                  ease: 'power2.out',
                })
                .to(child, {
                  duration: 0.1,
                  y: '10',
                  ease: 'power2.in',
                })
                .to(child, {
                  duration: 0.1,
                  y: '-5',
                  ease: 'power2.out',
                })
                .to(child, {
                  duration: 0.1,
                  y: '0',
                  ease: 'power2.in',
                })
                .to(child.scale, { duration: 0.1, x: 1, y: 1, ease: 'power2.in' })
            }

            resolve()
          }
          if (child instanceof Spine) {
            await sleep(650)
            this.asignMultiplierColor(this.multiplierArray[i], child)
          }
        })
      })
    })
  }

  public async showActiveMultiplier(): Promise<void> {
    const promises: Promise<void>[] = []

    for (const multiplierSymbol of this.multiplierReelRef.children) {
      if (multiplierSymbol.x === 0) {
        for (const child of multiplierSymbol.children ?? []) {
          if (child instanceof BitmapText) {
            this.multiplierHighLightNumberRef.alpha = 1
            this.multiplierHighLightNumberRef.fontSize = this.fontSize(this.multiplierArray[0])
            this.multiplierHighLightNumberRef.text = `x${this.multiplierArray[0]}`
            const multiplierHighLightPromise = new Promise<void>((resolve) => {
              gsap.timeline({ onComplete: resolve })
                .to(this.multiplierHighLightNumberRef.scale, { duration: 0.2, x: 0.7, y: 0.7, ease: 'power2.in' })
                .to(this.multiplierHighLightNumberRef.scale, { duration: 0.4, x: 1.8, y: 1.8, ease: 'power2.out' })
            })
            promises.push(multiplierHighLightPromise)

            child.alpha = 0
          }

          if (child instanceof Spine) {
            const currentAnimation = child.state.getCurrent(0)?.animation?.name
            if (!currentAnimation) return
            const actionName = currentAnimation.split('_').join('_action')
            const spinePromise = new Promise<void>((resolve) => {
              child.state.setAnimation(0, `${actionName}`, false)
              const duration = child.state.getCurrent(0)?.animation?.duration ?? 0
              setTimeout(resolve, duration * 1000)
            })
            promises.push(spinePromise)
          }
        }
      }
    }
    await Promise.all(promises)
  }

  public async removeActiveMultiplier() {
    const animations: Promise<void>[] = []

    this.multiplierReelRef.children.forEach((multiplierSymbol) => {
      if (multiplierSymbol.x === 0) {
        multiplierSymbol.children?.forEach(async (child) => {
          if (child instanceof BitmapText) {
            child.alpha = 0

            this.multiplierHighLightNumberRef.alpha = 1
            this.multiplierHighLightNumberRef.fontSize = this.fontSize(this.multiplierArray[0])
            this.multiplierHighLightNumberRef.text = `x${this.multiplierArray[0]}`
            const multiplierHighLightPromise = new Promise<void>((resolve) => {
              gsap.timeline()
                .to(this.multiplierHighLightNumberRef.scale, { duration: 0.3, x: 1, y: 1, ease: 'power2.in' })
                .to(this.multiplierHighLightNumberRef, { duration: 0.1, alpha: 0, onComplete: resolve })
            })
            animations.push(multiplierHighLightPromise)
          }

          if (child instanceof Spine) {
            const isThisSpinInSpeedMode = useGameStore.getState().isThisSpinInSpeedMode
            const currentAnimation = child.state.getCurrent(0)?.animation?.name
            if (!currentAnimation) return
            const outName = isThisSpinInSpeedMode ? currentAnimation.split('_').join('_out') : currentAnimation.split('_action').join('_out')
            child.state.setAnimation(0, `${outName}`, false)
            const duration = sleep(400)
            animations.push(duration)
          }
        })
      }
    })

    await Promise.all(animations)
  }

  public saveRemainReel() {
    this.multiplierReelRef.children.forEach(async (multiplierSymbol) => {
      const positionIndex = preciseDivide(multiplierSymbol.x, gameConfig.nodeWidth)
      multiplierSymbol.children?.forEach(async (child) => {
        if (child instanceof BitmapText) {
          const currentAmount = Number(child.text.replace(/\D/g, ''))
          this.remainArray[positionIndex] = currentAmount
        }
      })
    })
  }

  public setRemainReel() {
    this.multiplierReelRef.children.forEach(async (multiplierSymbol) => {
      const positionIndex = preciseDivide(multiplierSymbol.x, gameConfig.nodeWidth)
      multiplierSymbol.children?.forEach(async (child) => {
        if (child instanceof BitmapText) {
          const currentAmount = this.remainArray[positionIndex]
          child.text = `x${currentAmount}`
          child.fontSize = this.fontSize(currentAmount)
        }
        if (child instanceof Spine) {
          this.asignMultiplierColor(this.remainArray[positionIndex], child)
        }
      })
    })
  }
}

export const multiplierService = new MultiplierService()
